// src/hooks/useInstallationRequests.ts - Hook pour gérer les demandes d'installation

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  collection, 
  getDocs, 
  query, 
  orderBy, 
  doc, 
  updateDoc,
  Timestamp 
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { COLLECTIONS } from '../config/collections';
import { message } from 'antd';

export type InstallationRequestStatus = 'pending' | 'contacted' | 'installed' | 'cancelled';

export interface InstallationRequest {
  id: string;
  companyName: string;
  responsibleName: string;
  whatsapp: string;
  location: string;
  email?: string;
  forfait?: string;
  status: InstallationRequestStatus;
  createdAt: Date;
  source?: string;
  /** Ex. marketplace_pressing = candidature réseau ramassage */
  requestType?: string;
  notes?: string;
  contactedAt?: Date;
  installedAt?: Date;
  cancelledAt?: Date;
  cancelReason?: string;
}

// Hook pour récupérer toutes les demandes d'installation
export function useInstallationRequests() {
  return useQuery({
    queryKey: ['installationRequests'],
    queryFn: async (): Promise<InstallationRequest[]> => {
      const q = query(
        collection(db!, COLLECTIONS.installationRequests),
        orderBy('createdAt', 'desc')
      );
      
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          companyName: data.companyName,
          responsibleName: data.responsibleName,
          whatsapp: data.whatsapp,
          location: data.location,
          email: data.email,
          forfait: data.forfait,
          status: data.status || 'pending',
          createdAt: data.createdAt?.toDate() || new Date(),
          source: data.source,
          requestType: data.requestType,
          notes: data.notes,
          contactedAt: data.contactedAt?.toDate(),
          installedAt: data.installedAt?.toDate(),
          cancelledAt: data.cancelledAt?.toDate(),
          cancelReason: data.cancelReason,
        };
      });
    },
    enabled: !!db,
  });
}

// Hook pour mettre à jour le statut d'une demande
export function useUpdateInstallationRequestStatus() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ 
      id, 
      status, 
      notes 
    }: { 
      id: string; 
      status: InstallationRequestStatus;
      notes?: string;
    }) => {
      const docRef = doc(db!, COLLECTIONS.installationRequests, id);
      const updateData: any = { status };
      
      if (notes !== undefined) {
        updateData.notes = notes;
      }
      
      // Ajouter les timestamps appropriés selon le statut
      if (status === 'contacted') {
        updateData.contactedAt = Timestamp.now();
      } else if (status === 'installed') {
        updateData.installedAt = Timestamp.now();
      } else if (status === 'cancelled') {
        updateData.cancelledAt = Timestamp.now();
      }
      
      await updateDoc(docRef, updateData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['installationRequests'] });
      message.success('Statut mis à jour avec succès');
    },
    onError: (error) => {
      console.error('Erreur lors de la mise à jour du statut:', error);
      message.error('Erreur lors de la mise à jour du statut');
    },
  });
}

// Hook pour ajouter/modifier des notes
export function useUpdateInstallationRequestNotes() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, notes }: { id: string; notes: string }) => {
      const docRef = doc(db!, COLLECTIONS.installationRequests, id);
      await updateDoc(docRef, { notes });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['installationRequests'] });
      message.success('Notes mises à jour');
    },
    onError: (error) => {
      console.error('Erreur lors de la mise à jour des notes:', error);
      message.error('Erreur lors de la mise à jour des notes');
    },
  });
}

// Hook pour annuler une demande avec raison
export function useCancelInstallationRequest() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, cancelReason }: { id: string; cancelReason: string }) => {
      const docRef = doc(db!, COLLECTIONS.installationRequests, id);
      await updateDoc(docRef, { 
        status: 'cancelled',
        cancelledAt: Timestamp.now(),
        cancelReason 
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['installationRequests'] });
      message.success('Demande annulée');
    },
    onError: (error) => {
      console.error('Erreur lors de l\'annulation:', error);
      message.error('Erreur lors de l\'annulation');
    },
  });
}
